<template>
  <!-- 가격안내 페이지 에디터 -->
  <div class="pricing-page-editor">
    <!-- 상단: 제목 + 추가 버튼 -->
    <div class="pricing-page-editor__header">
      <h3 class="pricing-page-editor__title">요금제 관리</h3>
      <a-button
        type="primary"
        class="yellow-button"
        @click="handleAddPlan"
      >
        + 요금제 추가
      </a-button>
    </div>

    <!-- 빈 상태 -->
    <a-empty
      v-if="!pricingPlans || pricingPlans.length === 0"
      description="등록된 요금제가 없습니다."
      style="margin-top: 60px;"
    />

    <!-- 요금제 카드 그리드 -->
    <a-row v-else :gutter="16">
      <a-col
        v-for="(plan, index) in pricingPlans"
        :key="plan._id || plan.id || index"
        :span="12"
        style="margin-bottom: 16px;"
      >
        <a-card
          class="pricing-card"
          hoverable
          @click="handleEditPlan(plan, index)"
        >
          <!-- 카드 헤더: 이름 + 뱃지 -->
          <div class="pricing-card__header">
            <span class="pricing-card__name">{{ plan.name }}</span>
            <a-tag
              v-if="plan.badge"
              :style="{
                backgroundColor: plan.badgeBgColor || '#ffd700',
                color: plan.badgeTextColor || '#333',
                border: 'none',
                marginLeft: '8px',
              }"
            >
              {{ plan.badge }}
            </a-tag>
            <a-tag
              :color="plan.isActive ? 'green' : 'default'"
              style="margin-left: auto;"
            >
              {{ plan.isActive ? '활성' : '비활성' }}
            </a-tag>
          </div>

          <!-- 가격 정보 -->
          <div class="pricing-card__price-row">
            <span class="pricing-card__price">
              ₩{{ formatNumber(plan.price) }}<small>/월</small>
            </span>
            <span v-if="plan.discount" class="pricing-card__discount">
              {{ plan.discount }}% 할인
            </span>
          </div>

          <!-- 설치비 -->
          <div v-if="plan.setupFee" class="pricing-card__setup">
            설치비 ₩{{ formatNumber(plan.setupFee) }}
          </div>

          <!-- 설명 -->
          <div v-if="plan.description" class="pricing-card__description">
            {{ plan.description }}
          </div>

          <!-- 기능 목록 (최대 3개 + 더보기) -->
          <ul v-if="plan.features && plan.features.length" class="pricing-card__features">
            <li
              v-for="(feat, fi) in plan.features.slice(0, 3)"
              :key="fi"
              class="pricing-card__feature-item"
            >
              ✓ {{ feat }}
            </li>
            <li
              v-if="plan.features.length > 3"
              class="pricing-card__feature-more"
            >
              +{{ plan.features.length - 3 }}개 더 보기
            </li>
          </ul>

          <!-- 클릭 안내 -->
          <div class="pricing-card__edit-hint">
            <edit-outlined /> 클릭하여 편집
          </div>
        </a-card>
      </a-col>
    </a-row>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import { EditOutlined } from '@ant-design/icons-vue';

export default {
  name: 'PricingPageEditor',

  components: {
    EditOutlined,
  },

  computed: {
    ...mapGetters('homepageEditor', ['currentPageData']),

    // 현재 페이지 데이터 = PricingPlan 배열
    pricingPlans() {
      return this.currentPageData || [];
    },
  },

  methods: {
    ...mapMutations('homepageEditor', ['SET_ACTIVE_EDITOR']),

    // 새 요금제 추가 버튼 클릭
    handleAddPlan() {
      this.SET_ACTIVE_EDITOR({
        type: 'modal',
        sectionKey: 'pricingCard',
        payload: { plan: null, planIndex: -1 },
      });
    },

    // 기존 요금제 카드 클릭
    handleEditPlan(plan, planIndex) {
      this.SET_ACTIVE_EDITOR({
        type: 'modal',
        sectionKey: 'pricingCard',
        payload: { plan, planIndex },
      });
    },

    // 숫자 천 단위 구분 포맷
    formatNumber(value) {
      if (!value && value !== 0) return '0';
      return Number(value).toLocaleString('ko-KR');
    },
  },
};
</script>

<style scoped>
.pricing-page-editor__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}

.pricing-page-editor__title {
  font-size: 18px;
  font-weight: 600;
  color: #222;
  margin: 0;
}

.pricing-card {
  cursor: pointer;
  transition: box-shadow 0.2s;
}

.pricing-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.pricing-card__header {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.pricing-card__name {
  font-size: 16px;
  font-weight: 700;
  color: #111;
}

.pricing-card__price-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 4px;
}

.pricing-card__price {
  font-size: 22px;
  font-weight: 700;
  color: #222;
}

.pricing-card__price small {
  font-size: 13px;
  font-weight: 400;
  color: #888;
}

.pricing-card__discount {
  font-size: 13px;
  color: #e74c3c;
  font-weight: 600;
}

.pricing-card__setup {
  font-size: 12px;
  color: #888;
  margin-bottom: 8px;
}

.pricing-card__description {
  font-size: 13px;
  color: #555;
  margin-bottom: 12px;
  line-height: 1.5;
}

.pricing-card__features {
  list-style: none;
  padding: 0;
  margin: 0 0 12px;
}

.pricing-card__feature-item {
  font-size: 13px;
  color: #444;
  padding: 2px 0;
}

.pricing-card__feature-more {
  font-size: 12px;
  color: #999;
  font-style: italic;
}

.pricing-card__edit-hint {
  font-size: 12px;
  color: #bbb;
  text-align: right;
  margin-top: 8px;
}
</style>
